import { useState } from 'react';

import { ProfileCardProps, TProfileUserCard, User } from '@/API/models';
import { SUBSCRIPTION_PLANS_CONFIG } from '@/components/SubscriptionCard/SubscriptionCard.utils';
import {
  formatCurrency,
  formatDate,
  userSuscriptionBackground,
} from '@/Utils/helpers';
import { useCurrentUser } from '@/Utils/stripeHooks/useCurrentUser';

export const userRoles: Record<User['role'], TProfileUserCard['role']> = {
  user: 'User',
  operator: 'Operator',
  admin: 'Admin',
  evolux_partner: 'Partner',
};

export const useProfileNavigation = ({ logo }: { logo: string }) => {
  const { data: user } = useCurrentUser();

  const isUserRole = user?.role === 'user';

  const subscription = user?.subscription;
  const plan_name = subscription?.plan_name;
  const interval = subscription?.interval === 'month' ? 'monthly' : 'yearly';
  const member_since =
    isUserRole && subscription?.created_at
      ? formatDate(new Date(subscription?.created_at))
      : undefined;

  const background = userSuscriptionBackground(
    user?.subscription?.plan_name ?? 'Free',
  );

  const userCard: TProfileUserCard = {
    icon: logo,
    role: userRoles[user?.role || 'user'],
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    member_since: member_since,
  };
  const planConfig = plan_name
    ? SUBSCRIPTION_PLANS_CONFIG[plan_name]
    : SUBSCRIPTION_PLANS_CONFIG['Free'];
  const plan_img = planConfig.iconSrcLarge;

  const formatedDate = subscription?.current_period_end
    ? formatDate(new Date(subscription?.current_period_end))
    : '';
  const nextInvoice = subscription?.created_at
    ? !subscription?.cancel_at_period_end
      ? `Next invoice: ${formatedDate}`
      : `Active until: ${formatedDate}`
    : null;

  const cards: ProfileCardProps[] = [
    {
      icon: plan_img,
      title: 'Active plan',
      labelPrimary: subscription ? subscription?.plan_name : 'FREE',
      button: 'Upgrade',
      type: 'upgrade',
    },
    {
      icon: '/icons/balance.svg',
      title: 'Available Balance',
      infoIcon: '/icons/info-white.svg',
      labelPrimary: formatBalance(user?.stripe_balance),

      button: 'Top Up',
    },
    {
      icon: '/icons/price.svg',
      title: 'Price',
      labelPrimary: `$${planConfig.price[interval] === 0 ? '00.00' : planConfig.price[interval]} / `,
      labelSecondary: subscription ? subscription.interval : 'month',
      smallSecondaryLabel: true,
      description: nextInvoice,
    },
  ];

  const [profileCardsExpanded, setProfileCardsExpanded] = useState(false);
  const handleToggleCards = () => {
    setProfileCardsExpanded((prev) => !prev);
  };

  return {
    cards,
    userCard,
    profileCardsExpanded,
    background,
    isUserRole,
    handleToggleCards,
    setProfileCardsExpanded,
  };
};

const formatBalance = (balance?: number | null): string => {
  if (balance === 0 || balance == null) {
    return '$00,000.00';
  }
  return formatCurrency(balance);
};
